/**
 * Which version of the app is this? Two answers, because an installed PWA can keep
 * serving a cached bundle for days:
 *
 *   RUNNING_BUILD   compiled into this bundle (vite define): what is on screen
 *   /web/build.json deployed beside the bundle, fetched with no-store: what is live now
 *
 * Same commit means current; a different one means this browser holds an older bundle.
 * A build made from uncommitted code carries a "+local" commit and is never "current".
 * The API has its own /api/build.json, written by scripts/deploy/deploy-live.cjs.
 */

export interface BuildInfo {
  /** Short git hash, with "+local" when built from uncommitted code. */
  commit: string;
  /** ISO timestamp of the build (web) or the deploy (API). */
  builtAt: string;
}

export type VersionStatus =
  | { status: 'current' }
  | { status: 'outdated'; live: BuildInfo }
  | { status: 'local' }
  | { status: 'unknown' };

declare const __BUILD__: BuildInfo;
export const RUNNING_BUILD: BuildInfo = __BUILD__;

/** True when `x` has the shape of a build stamp; anything fetched is checked before use. */
export function isBuildInfo(x: unknown): x is BuildInfo {
  if (typeof x !== 'object' || x === null) return false;
  const o = x as Record<string, unknown>;
  return typeof o.commit === 'string' && o.commit !== '' && typeof o.builtAt === 'string';
}

/** Compare the running bundle with what is deployed. `live` is null when it could not be read. */
export function versionStatus(running: BuildInfo, live: BuildInfo | null): VersionStatus {
  if (running.commit.endsWith('+local')) return { status: 'local' };
  if (live === null) return { status: 'unknown' };
  return live.commit === running.commit ? { status: 'current' } : { status: 'outdated', live };
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "25 Sep 2026" in local time; the input itself when it is not a valid date. */
export function formatBuildDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${String(d.getDate()).padStart(2, '0')} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/** "734a93a · 25 Sep 2026" — what a person reads out on a support call. */
export function buildLabel(b: BuildInfo): string {
  return `${b.commit} · ${formatBuildDate(b.builtAt)}`;
}

/** Fetch a deployed build.json fresh (never from any cache). Null when missing or malformed. */
export async function fetchBuildInfo(url: string, signal?: AbortSignal): Promise<BuildInfo | null> {
  try {
    const res = await fetch(`${url}?t=${Date.now()}`, { cache: 'no-store', signal });
    if (!res.ok) return null;
    const body: unknown = await res.json();
    return isBuildInfo(body) ? body : null;
  } catch {
    return null; // offline, or not deployed with the stamp yet
  }
}
